'use client'
import React, { useEffect, useRef, useState } from 'react'
import { getData } from '../lib/index'
import { getCart, postCart } from '../repositories/cartRepository'
import withApiUrl from '../utils/apiUrlHoc'
import { CART, PRODUCT_DETAILS, PRODUCT_LIST } from '../utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import { usePathname } from 'next/navigation'
import { updateAddress } from '../reduxToolkit/addressSlice'

export const CartContext = React.createContext({
  cart: {},
  previousCart: {},
  error: '',
  orderAmount: 0,
  totalPayable: 0,
  totalDiscount: 0,
  shippingCharge: 0,
  totalProductsCount: 0,
  clickAndCollectCharge: 0,
  isSideCartVisible: false,
  clearCart: () => {},
  updateCart: () => {},
  getProductCount: () => {},
  getBundleData: () => {},
  getTotalCountByProductId: () => {},
  updateProductQuantity: () => {},
  showCartItemsChangePopup: false,
  handleCartItemsChangePopup: () => {},
  updatedCart: {},
  isItemAddedToCart: false,
  updateCartUsingGetCart: () => {},
  handleSideCartVisibility: () => {},
  handleCartSlider: () => {},
})

function CartProvider(props) {
  const addressContext = useSelector((state) => state.address)
  const { organizationData } = useSelector((state) => state.commonData)
  const [state, updateState] = useState({
    cart: {},
    previousCart: {},
    error: '',
    loading: false,
    renderCart: false,
    oldAddress: addressContext?.address,
    oldHub: addressContext?.hub,
    showCartItemsChangePopup: false,
    updatedCart: {},
  })
  const [isItemAddedToCart, setItemAddedToCart] = useState(false)
  const [showCartSlider, setShowCartSlider] = useState(false)
  const accountContext = useSelector((state) => state.auth)
  const prevProps = useRef({})
  const pendingReq = useRef(new Map())
  const cartLoadedOnMount = useRef(false)
  const pathname = usePathname()

  const [isSideCartVisible, setIsSideCartVisible] = useState(false)
  const dispatch = useDispatch()

  const handleSideCartVisibility = (status) => {
    setIsSideCartVisible(status)
  }

  // Close side cart visibilty on route change
  useEffect(() => {
    setIsSideCartVisible(false)
  }, [pathname])

  const setState = (data) => {
    updateState({
      ...state,
      ...data,
    })
  }

  /**
   * @description This replaces the whole cart with cart provided in action and is used by checkout to update the cart finally
   */
  const updateCart = (action) => {
    // To replace cart
    const { value } = action
    setState({
      cart: value,
    })
  }

  /**
   * @description This empties the whole cart
   */
  const clearCart = () => {
    setState({
      cart: {},
    })
  }

  const handleCartItemsChangePopup = (action) => {
    if (action === 'change') {
      setState({
        cart: state.updatedCart,
        showCartItemsChangePopup: false,
        oldAddress: addressContext?.address,
        oldHub: addressContext?.hub,
      })
    } else {
      const payload = {
        address: {
          ...state.oldAddress,
          hub: state.oldHub,
          isAddressPresent: true,
        },
      }
      dispatch(updateAddress(payload))
      setState({
        cart: state.previousCart,
        showCartItemsChangePopup: false,
      })
    }
  }

  const handleGetCart = async () => {
    const { address } = addressContext
    const { userData } = accountContext
    const { accessToken } = userData
    if (
      address?.storeId ||
      (state?.oldAddress && Object.keys(state.oldAddress).length)
    ) {
      try {
        // Clear cart offers when navigating from checkout to home and moving to cart. Checkout has offers, but the cart does not.
        setState({
          cart: {
            ...state.cart,
            offers: [],
          },
        })
        const cart = await getCart(address, { accessToken })
        if (state.oldAddress?.storeId !== addressContext.address?.storeId) {
          if (!state.oldAddress?.storeId) {
            setState({
              cart: state.cart,
              oldAddress: addressContext?.address,
              oldHub: addressContext?.hub,
            })
          } else if (cart?.items?.length < state.cart?.items?.length) {
            setState({
              showCartItemsChangePopup: true,
              updatedCart: cart,
              previousCart: state.cart,
            })
            return
          }
        }
        setState({
          cart,
          oldAddress: addressContext?.address,
          oldHub: addressContext?.hub,
        })
      } catch (error) {
        console.error(error)
      }
    }
  }

  const getCartPostData = (data, parentContainer) => {
    let items = data
    const isMarketPlace = organizationData?.config?.website?.isMarketPlace
    // Send the whole cart if the user is not signed in
    if (!accountContext?.userData?.accessToken) {
      if (pendingReq.current.size > 0) {
        for (const [, value] of pendingReq.current) {
          items.push(value)
        }
      }
    }
    items = mergeItems(state?.cart?.items || [], items, parentContainer)
    const newItems = items.map((val) => {
      return {
        q: `${val.q}`,
        id: `${val.id}`,
        t: val.t,
        bundleItems: val?.bundleItems,
        ...(isMarketPlace && val?.sellerID && { sellerID: val.sellerID }),
      }
    })
    return newItems
  }

  const handlePostCart = async () => {
    const { address } = addressContext
    const { userData } = accountContext
    const { accessToken } = userData
    const items = getCartPostData(state?.cart?.items || [])
    try {
      const cart = await postCart(address, items, {
        accessToken,
      })
      setState({ cart })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @description Update if a product has been added to cart in the backend
   */
  const updateProductSyncStatus = ({ item, status }) => {
    const pendingProducts = pendingReq.current
    const id = +item.id
    if (status === 'PENDING') {
      pendingProducts.set(id, item)
    } else if (status === 'COMPLETE') {
      pendingProducts.delete(+id)
    }
  }

  /**
   * @description Add, update or delete an item in cart
   */
  const updateProductQuantity = async (item, parentContainer) => {
    const { id, q } = item

    const pendingProducts = pendingReq.current
    // If product quantity goes 1 from 2 no need to show Item added to cart
    const isProductQuantityOneFromTwo = state?.cart?.items?.some((product) => {
      return product.id === id
    })

    // If request already in pending then do not make new request
    if (pendingProducts.has(id)) {
      // Trying to update a product which is pending
      return undefined
    }

    const { address } = addressContext
    const { userData } = accountContext
    const { accessToken } = userData
    const req = await postCart(
      address,
      getCartPostData([item], parentContainer),
      {
        accessToken,
      }
    )
    updateProductSyncStatus({ item, status: 'PENDING' })
    try {
      const nextCart = await req
      updateProductSyncStatus({ item, status: 'COMPLETE' })
      const updatedItem = (nextCart.items || []).find((x) => x.id === id)
      // DONT UPDATE PENDING PRODUCTS
      const mergedCart = getMergedCart(state?.cart, nextCart, pendingProducts)
      setState({ cart: mergedCart })
      if (Number(q) === 1 && !isProductQuantityOneFromTwo) {
        setItemAddedToCart(true)
      }
      setTimeout(() => {
        setItemAddedToCart(false)
      }, 5000)
      return updatedItem
    } catch (err) {
      updateProductSyncStatus({ item, status: 'COMPLETE' })
      console.error(err)
    }

    return undefined
  }

  /**
   * @description Returns the number of items in a cart for a product
   * if offer limit reaches we get splitted results for the same product id in the cart.
   * if item is in the cart count is calculated based on item.index
   * if item is in productCollectionList then we need to show total count of splitted products or single product based on id
   */
  const getProductCount = (
    uniqueKey,
    parentContainer,
    productId,
    sellerID = ''
  ) => {
    const items = state?.cart?.items || []
    if (
      parentContainer === PRODUCT_LIST ||
      parentContainer === PRODUCT_DETAILS ||
      parentContainer === CART
    ) {
      let res = 0
      items.forEach((item) => {
        if (
          item.id === productId &&
          (!sellerID || item?.sellerID === sellerID) &&
          !item.isFree
        ) {
          res += parseInt(item.q, 10)
        }
      })
      return res
    }
    return 0
  }

  const getTotalCountByProductId = (productId, sellerID = '') => {
    let count = 0
    const items = state?.cart?.items || []
    items.forEach((item) => {
      if (item.id === productId && (!sellerID || item?.sellerID === sellerID)) {
        count += parseInt(item.q, 10)
      }
    })

    return count
  }

  const getBundleData = (productId) => {
    const items = state?.cart?.items || []
    const item = items.find((item) => item.id === productId)
    return item ? item?.bundleItems : null
  }

  const changeRenderCartStatus = (status) => {
    setState({
      renderCart: status,
    })
  }

  const handleCartSlider = (event) => {
    if (event) event.stopPropagation()
    setShowCartSlider(!showCartSlider)
  }
  // if address changes then post cart
  // to update cart
  useEffect(() => {
    const { address } = addressContext
    if (cartLoadedOnMount.current) {
      if (address?.address) {
        handlePostCart()
      }
    }
    if (state.oldHub !== addressContext.hub) {
      setState({
        oldHub: addressContext?.hub,
      })
    }
  }, [addressContext.address, cartLoadedOnMount.current, addressContext.hub])

  useEffect(() => {
    // If logout user, clear cart
    const { isLoggedin } = accountContext
    if (prevProps.current.isLoggedin && !isLoggedin) {
      prevProps.current.isLoggedin = false
      clearCart()
      return
    }

    // if user login
    // post cart if there is cart
    // get cart if cart empty
    if (!prevProps.current.isLoggedin && isLoggedin) {
      cartLoadedOnMount.current = false
      if (state?.cart?.items) {
        handlePostCart()
      } else {
        handleGetCart()
      }
    }

    if (
      state.oldAddress?.storeId !== addressContext.address?.storeId &&
      isLoggedin
    ) {
      handleGetCart()
    }

    // If user not logged in then initialize cart from local storage
    if (!isLoggedin) {
      const cart = JSON.parse(getData('cart')) || {}
      setState({ cart })
      cartLoadedOnMount.current = true
    }

    prevProps.current.isLoggedin = accountContext.isLoggedin // update prev props
  }, [addressContext.address, accountContext.isLoggedin])

  const syncCartAppWeb = async () => {
    const { address } = addressContext
    const { userData } = accountContext
    const { accessToken } = userData
    if (address?.storeId && userData.isLoggedin) {
      const cart = await getCart(address, { accessToken })
      // updating cart if fetched cart has different items count
      if (cart.items.length !== state.cart.items?.length) {
        setState({ cart })
        return
      }
      // updating cart if any item quantity is different than fetched items quantity
      cart.items.forEach((item) => {
        if (item.q !== state.cart.items.filter((i) => i.id === item.id)[0]?.q) {
          setState({ cart })
        }
      })
    }
  }
  const updateCartUsingGetCart = () => {
    handleGetCart()
  }
  useEffect(() => {
    if (['/cart', '/checkout'].includes(pathname)) syncCartAppWeb()
  }, [pathname])

  const {
    cart,
    loading,
    error,
    showCartItemsChangePopup,
    updatedCart,
    renderCart,
  } = state
  return (
    <CartContext.Provider
      value={{
        cart,
        previousCart: state.previousCart,
        error,
        loading,
        clearCart,
        updateCart,
        getProductCount,
        getBundleData,
        getTotalCountByProductId,
        updateProductQuantity,
        updateCartUsingGetCart,
        orderAmount: getOrderAmount(state.cart),
        totalPayable: getTotalPayable(state.cart),
        totalDiscount: getTotalDiscount(state.cart),
        shippingCharge: getShippingCharge(state.cart),
        totalProductsCount: getTotalProductsCount(state.cart),
        clickAndCollectCharge: getClickAndCollectCharge(state.cart),
        renderCart,
        changeRenderCartStatus,
        showCartItemsChangePopup,
        handleCartItemsChangePopup,
        updatedCart,
        isItemAddedToCart,
        isSideCartVisible,
        handleSideCartVisibility,
        handleCartSlider,
        showCartSlider,
      }}
    >
      {props.children}
    </CartContext.Provider>
  )
}

// to get the total products count
function getTotalProductsCount(cart) {
  const items = cart?.items || []
  let count = 0
  for (let i = 0; i < items.length; i += 1) {
    count += +items[i].q
  }
  return count
}

// To get the payable amount
function getTotalPayable(cart) {
  const youPay = cart?.youPay || 0
  return youPay
}

// To get the total discount
function getTotalDiscount(cart) {
  const savings = cart?.savings || 0
  return savings
}

// To get click and collect charges
function getClickAndCollectCharge(cart) {
  const amounts = cart?.amounts
  if (amounts) {
    return amounts.clickAndCollectCharge || 0
  }
  return 0
}

// To get delivery or shipping charges
function getShippingCharge(cart) {
  const amounts = cart?.amounts
  if (amounts) {
    return amounts.shippingAmount || 0
  }
  return 0
}

function getOrderAmount(cart) {
  const amounts = cart?.amounts
  if (amounts) {
    return amounts.orderAmount || 0
  }
  return 0
}

function getMergedCart(currentCart, nextCart, pendingProducts) {
  const mergedCart = { ...nextCart }
  let nextItems = mergedCart.items || []
  nextItems = nextItems.filter((x) => {
    const res = !pendingProducts.has(+x.id)
    return res
  })
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of pendingProducts) {
    const index = (currentCart?.items || []).findIndex((x) => +x.id === key)
    if (index >= 0) {
      const item = currentCart.items[index]
      const updatedItem = { ...item, ...value }
      if (updatedItem?.q > 0) nextItems.push(updatedItem)
    }
  }

  mergedCart.items = nextItems
  mergedCart.items.sort(sortByName)
  return mergedCart
}

function sortByName(a, b) {
  const nameA = (a?.product?.fullName || a?.product?.name).toUpperCase() // ignore upper and lowercase
  const nameB = (b?.product?.fullName || b?.product?.name).toUpperCase() // ignore upper and lowercase
  if (nameA < nameB) {
    return -1
  }
  if (nameA > nameB) {
    return 1
  }

  // names must be equal
  return 0
}

/**
 * @param {Array} previous | Array of existing cart items
 * @param {Array} current | Array of cart items to be added
 */

function mergeItems(previous = [], current = [], parentContainer) {
  const res = [...previous]
  // if cart is empty then directly push into result
  if (previous?.length === 0) {
    res.push(...current)
  } else {
    current.forEach((item) => {
      /*
       cart may has multiple items with same id so 
       for that case we've added uniqueKey to each item in postcart&getcart 
       methods and so updating based  item.uniqueKey
      */
      if (item.uniqueKey && parentContainer === CART) {
        const index = res.findIndex((x) => x.uniqueKey === item.uniqueKey)
        res[index] = item
      } else {
        const removeValFromIndex = []
        res.forEach((val, index) => {
          if (item.id === val.id) {
            removeValFromIndex.push(index)
          }
        })

        if (removeValFromIndex.length > 1) {
          for (let i = removeValFromIndex.length - 1; i >= 0; i--) {
            res.splice(removeValFromIndex[i], 1)
          }
          res.push(item)
        } else {
          /*
       if item is added from productlist/productslider in that case 
       it doesn't have uniqueKey so we need to update  by finding index through id
       */
          const index = res.findIndex((x) => x.id === item.id && !item.isFree)
          if (index > -1) {
            res[index] = item
          } else {
            res.push(item)
          }
        }
      }
    })
  }
  const result = res.filter((val) => !val.isFree)
  return result
}

export default withApiUrl(CartProvider)
